import firebaseConfig from '@/config/firebase/firebaseConfig.json';
import { getAppType } from '@/lib/offline/OfflineAppHelper';
import * as Sentry from '@sentry/react';
import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  Auth,
  browserLocalPersistence,
  getAuth,
  indexedDBLocalPersistence,
  setPersistence,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth';
import { Database, getDatabase } from 'firebase/database';
import {
  FieldValue,
  Firestore,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  persistentSingleTabManager,
  waitForPendingWrites,
} from 'firebase/firestore';

/**
 * APL-11850 (FS-01 on quit/relaunch): lockdown environments host exactly ONE
 * tab/WebView, so multi-tab primary-lease election is pure cost there — and an
 * abruptly killed session (lockdown quit never runs the logout teardown)
 * leaves its IndexedDB connection + primary lease behind. The next launch
 * then waits out the zombie lease INSIDE the boot deadlines, which is QA's
 * "FS-01 for 2-4 successive sessions until it recovers" signature.
 * `persistentSingleTabManager({ forceOwnership: true })` takes the lease
 * immediately instead — the SDK's designed answer for single-tab environments.
 * Browsers (getAppType() === 'web') keep the multi-tab manager — invigilators
 * legitimately run multiple tabs. Every non-web appType (ios / mac / seb /
 * win / cros) is a single-window lockdown host by construction.
 */
const isLockdownApp = (): boolean => getAppType() !== 'web';

// Define types for the returned services for better type safety
export interface FirebaseServices {
  firestoreApp: FirebaseApp;
  firestoreDB: Firestore;
  firebaseAuth: Auth;
  firebaseRealtimeDb: Database;
  waitForPendingWrites: any;
  firebaseSignout: () => Promise<void>;
  ensureSignedInAs: (token: string, username: string, options?: any) => Promise<any>;
  // firestoreFieldValue: typeof FieldValue; // Or a more specific type if you adjust imports
}

let firebaseServices: FirebaseServices | null = null;

/**
 * Explicit Firebase teardown. Call this ONLY from the AssessPrep logout /
 * `ap-token`-clear path — never from a React effect cleanup. Effect cleanups
 * fire on every re-render / unmount and would race a fresh sign-in; the auth
 * session must outlive any single component.
 */
const firebaseSignout = async (): Promise<void> => {
  if (!firebaseServices) return;
  try {
    await signOut(firebaseServices.firebaseAuth);
  } catch (error) {
    // Don't rethrow — this runs on the logout path and logout must always
    // complete even if the Firebase signout hiccups. Capture for telemetry
    // instead of a console-only log so a (rare) failure still surfaces.
    Sentry.captureException(error, { tags: { context: 'firebase_signout' } });
  }
};

/**
 * Identity-aware sign-in. Guarantees the active Firebase session matches
 * `username` after it resolves:
 *  - same identity already signed in → no-op, returns the existing user.
 *  - a different identity is signed in (e.g. View-As) → sign out first, then
 *    sign in with the new custom token. The unconditional "skip if currentUser
 *    is set" short-circuit this replaces was the View-As race: it kept the prior
 *    user's session alive after switching identities.
 *  - nobody signed in → sign in with the custom token.
 */
const ensureSignedInAs = async (
  token: string,
  username: string,
  options: any = {}
): Promise<any> => {
  if (!firebaseServices) return;
  const { firebaseAuth } = firebaseServices;
  try {
    if (!options.persistMultitab) {
      // Prefer IndexedDB persistence over session/local storage. WebKit ITP /
      // the 7-day storage cap evicts session-storage-backed auth aggressively;
      // IndexedDB survives longer, which matters for long exam sessions. Fall
      // back to local-storage persistence when IndexedDB is unavailable
      // (private-mode Safari, some sandboxed WebViews).
      try {
        await setPersistence(firebaseAuth, indexedDBLocalPersistence);
      } catch {
        await setPersistence(firebaseAuth, browserLocalPersistence);
      }
    }

    const currentUser = firebaseAuth.currentUser;
    if (currentUser?.email === username) {
      // Already signed in as the right identity — don't re-sign-in.
      return currentUser;
    }
    if (currentUser) {
      // Identity changed (View-As / re-login) — tear down the stale session first.
      await signOut(firebaseAuth);
    }

    return await signInWithCustomToken(firebaseAuth, token);
  } catch (error) {
    console.error('Error signing in: ', error);
    return Promise.reject(error);
  }
};

/**
 * Initializes the Firebase application and its services.
 * This function ensures that Firebase is initialized only once.
 * @returns An object containing the initialized Firebase services.
 */
const initializeFirebaseApp = (): FirebaseServices => {
  // Check if the app is already initialized to prevent re-initialization
  if (firebaseServices) {
    return firebaseServices;
  }
  // removing this and initializing it outside of the function to allow signout
  const app: FirebaseApp = initializeApp(firebaseConfig);

  let db: Firestore;

  try {
    // Try to initialize with persistence enabled
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        // Lockdown hosts: single-tab with immediate lease ownership — see
        // isLockdownApp above. Plain browsers keep multi-tab.
        tabManager: isLockdownApp()
          ? persistentSingleTabManager({ forceOwnership: true })
          : persistentMultipleTabManager(),
        cacheSizeBytes: 1024 * 1024 * 1024, // 1GB — keep Firestore LRU GC active
      }),
      // SDK strips undefined values at the wire level. We still observe + report
      // them via observeUndefinedFields so the data-integrity signal isn't lost.
      ignoreUndefinedProperties: true,
      /**
       * Options that configure the SDK’s underlying network transport (WebChannel)
       * when long-polling is used.
       *
       * These options are only used if `experimentalForceLongPolling` is true or if
       * `experimentalAutoDetectLongPolling` is true and the auto-detection
       * determined that long-polling was needed. Otherwise, these options have no
       * effect.
       */
      //experimentalAutoDetectLongPolling: false,

      /**
       * The desired maximum timeout interval, in seconds, to complete a
       * long-polling GET response. Valid values are between 5 and 30, inclusive.
       * Floating point values are allowed and will be rounded to the nearest
       * millisecond.
       *
       * By default, when long-polling is used the "hanging GET" request sent by
       * the client times out after 30 seconds. To request a different timeout
       * from the server, set this setting with the desired timeout.
       *
       * Changing the default timeout may be useful, for example, if the buffering
       * proxy that necessitated enabling long-polling in the first place has a
       * shorter timeout for hanging GET requests, in which case setting the
       * long-polling timeout to a shorter value, such as 25 seconds, may fix
       * prematurely-closed hanging GET requests.
       * For example, see https://github.com/firebase/firebase-js-sdk/issues/6987.
       */
      experimentalLongPollingOptions: { timeoutSeconds: 15 },
    });
    console.log('Firestore offline persistence enabled.');
  } catch (error: any) {
    // Persistence can fail for various reasons: private browsing, storage quota exceeded,
    // browser incompatibility, or (rarely) multi-tab lock contention. In all cases,
    // fall back gracefully to online-only mode so the app remains functional.
    console.warn('Firestore persistence unavailable, falling back to online-only mode:', error);

    Sentry.captureException(error, {
      tags: {
        firestore_fallback: 'online_only',
        error_code: error?.code ?? 'unknown',
      },
    });
    // Fallback to online-only mode. MUST still pass `ignoreUndefinedProperties: true`
    // here — without it, this Firestore instance throws on any write containing
    // an `undefined` value, and the new flow (firestoreDataProvider.createMany +
    // useRealtimeDbBatchMutation) no longer has a retry/sanitize layer that
    // could recover. Multi-tab users (common for invigilators monitoring multiple
    // assessments) and private-mode users would otherwise hit the original
    // APL-10106 failure mode on every write.
    //
    // `initializeFirestore` can only be called once per app; if the try block
    // above already succeeded in calling it (settings persist on the app
    // instance), a second call throws — fall back to `getFirestore(app)` which
    // returns the previously-initialized instance with its settings intact.
    try {
      db = initializeFirestore(app, { ignoreUndefinedProperties: true });
    } catch {
      db = getFirestore(app);
    }
  }

  const waitForPendingWritesService: any = async () => {
    return await waitForPendingWrites(db);
  };

  const auth: Auth = getAuth(app);

  const realtimeDb: Database = getDatabase(app);

  // Regarding FieldValue:
  // In Firestore SDK v9+, specific field value generators like serverTimestamp(),
  // arrayUnion(), arrayRemove(), increment(), deleteField() are imported directly
  // from 'firebase/firestore' and used.
  // For example: import { serverTimestamp } from 'firebase/firestore';
  // const data = { createdAt: serverTimestamp() };
  //
  // If 'FieldValue' here refers to the abstract type for type hinting, that's fine.
  // If it's meant to be the object holding static methods like `FieldValue.serverTimestamp()`
  // from older SDKs, the v9+ equivalent is to import and use the specific functions.
  // For clarity, if you need the actual FieldValue class/object from a specific import path
  // that provides static methods (less common in v9+ for user code), ensure the import is correct.
  // For now, I'll assume 'FieldValue' is the type as imported.
  const fieldValueService = FieldValue;

  firebaseServices = {
    firestoreApp: app,
    firestoreDB: db,
    firebaseAuth: auth,
    firebaseRealtimeDb: realtimeDb,
    firebaseSignout: firebaseSignout,
    ensureSignedInAs: ensureSignedInAs,
    waitForPendingWrites: waitForPendingWritesService,
    // firestoreFieldValue: fieldValueService,
  };

  return firebaseServices;
};

// const firebaseSignout = () => {
//   const auth = getAuth();
//   signOut(auth);
// };

// You can also export the individual services if you initialize them elsewhere
// or want to allow consumers to potentially get them if already initialized.
// However, the primary export is the initializer function.
// const firebaseServices: FirebaseServices = initializeFirebaseApp();
// firebaseServices = initializeFirebaseApp();
export { ensureSignedInAs, firebaseServices, firebaseSignout, initializeFirebaseApp };

// Example of how you might want to export individual pre-initialized services
// (though the function pattern is often preferred for controlled initialization):

/*
let services: FirebaseServices | null = null;

const getFirebaseServices = (): FirebaseServices => {
  if (!services) {
    services = initializeFirebaseApp(); // Initialize on first call
  }
  return services;
}

// Then you could export individual getters or the getter function itself
export const getFirestoreApp = () => getFirebaseServices().firestoreApp;
export const getFirestoreDB = () => getFirebaseServices().firestoreDB;
// etc.
*/
